import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDomain } from '../context/DomainContext';
import { Box, Alert, Button, Paper, Typography, CircularProgress } from '@mui/material';
import LoadingSplash from './LoadingSplash';

const ProtectedRoute = ({ 
    children, 
    adminOnly = false, 
    requireOnboarding = true
}) => {
    const { 
        user, 
        userProfile,  // Usa userProfile invece di profile
        loading: authLoading,
        needsProjectRegistration,
        registerForCurrentProject 
    } = useAuth();
    const { loading: domainLoading, config } = useDomain();
    const location = useLocation();
    const navigate = useNavigate();

    // Mostra loading durante caricamento
    if (authLoading || domainLoading) {
        return <LoadingSplash />;
    }

    // Verifica autenticazione
    if (!user) {
        console.log('[ProtectedRoute] User not authenticated, redirecting to login');
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // Verifica profilo - SOLO se il profilo non esiste E sappiamo che serve registrazione
    if (!userProfile) {
        console.log('[ProtectedRoute] No profile found for user');
        
        // Se stiamo ancora caricando, mostra loading
        if (!needsProjectRegistration && !authLoading) {
            // Potrebbe essere ancora in caricamento, aspetta un attimo
            return (
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '80vh'
                }}>
                    <CircularProgress />
                </Box>
            );
        }
        
        // Solo se siamo sicuri che serve registrazione
        if (needsProjectRegistration) {
            const handleRegisterForProject = async () => {
                console.log('[ProtectedRoute] Attempting to register user for project');
                if (registerForCurrentProject) {
                    const result = await registerForCurrentProject();
                    if (result?.profile) {
                        // Registrazione riuscita, naviga a onboarding se necessario
                        if (!result.profile.is_onboarding_completed) {
                            navigate('/onboarding');
                        } else {
                            navigate('/dashboard');
                        }
                    }
                }
            };

            return (
                <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: '80vh'
                }}>
                    <Paper sx={{ p: 4, maxWidth: 500, width: '100%' }}>
                        <Typography variant="h5" gutterBottom>
                            Registrazione Richiesta
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Sei autenticato come <strong>{user.email}</strong>, 
                            ma non sei ancora registrato per il progetto 
                            <strong> {config?.project_name || 'corrente'}</strong>.
                        </Typography>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Per accedere a questo progetto, devi completare la registrazione 
                            e il processo di onboarding.
                        </Alert>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button 
                                variant="outlined" 
                                onClick={() => navigate('/')}
                            >
                                Torna alla Home
                            </Button>
                            <Button 
                                variant="contained" 
                                onClick={handleRegisterForProject}
                                color="primary"
                            >
                                Registrati per questo Progetto
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            );
        }
    }

    // A questo punto abbiamo un profilo valido
    const profile = userProfile;

    // Controllo onboarding se richiesto
    if (requireOnboarding && profile) {
        const onboardingCompleted = profile.is_onboarding_completed === true;
        
        console.log('[ProtectedRoute] Onboarding check:', {
            path: location.pathname,
            is_onboarding_completed: profile.is_onboarding_completed,
            onboardingCompleted,
            project_name: profile.project_name
        });
        
        // Se onboarding non completato E non siamo già su /onboarding
        if (!onboardingCompleted && location.pathname !== '/onboarding') {
            console.log('[ProtectedRoute] Onboarding not completed, redirecting to /onboarding');
            return <Navigate to="/onboarding" replace state={{ from: location }} />;
        }

        // Se l'onboarding è completato e siamo su /onboarding, redirect a dashboard
        if (onboardingCompleted && location.pathname === '/onboarding') {
            console.log('[ProtectedRoute] Onboarding already completed, redirecting to dashboard');
            return <Navigate to="/UserDashboard" replace />;
        }
    }

    // Controllo admin (se richiesto)
    if (adminOnly && profile) {
        const isAdmin = profile.roles && (
            profile.roles.includes('platform_admin') || 
            profile.roles.includes('project_admin') || 
            profile.roles.includes('brand_admin') || 
            profile.roles.includes('organization_admin')
        );
        
        if (!isAdmin) {
            console.log('[ProtectedRoute] Admin access required but user is not admin. Roles:', profile.roles);
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Alert severity="warning">
                        Accesso negato. Sono richiesti privilegi di amministratore.
                    </Alert>
                </Box>
            );
        }
    }

    // Tutto OK, mostra contenuto protetto
    console.log('[ProtectedRoute] Access granted to:', location.pathname);
    return children;
};

export default ProtectedRoute;